import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { http } from '@/lib/api/client'
import type { Resource, Staff, StaffInvitation, StaffRole } from '@/lib/api/types'

/** Support work per staff member over the last `days` days, keyed by staff id. */
export interface TicketStats {
  days: number
  by_staff: Record<
    string,
    {
      replies: number
      tickets: number
      /** Tickets they replied to first, and the median wait for that reply. */
      first_replies: number
      median_first_reply_minutes: number | null
      open_assigned: number
      resolved: number
    }
  >
}

export function useStaff() {
  return useQuery({
    queryKey: ['staff'],
    queryFn: () =>
      http.get<{ data: Staff[]; invitations: StaffInvitation[]; ticket_stats?: TicketStats }>('/staff'),
  })
}

export function useStaffAction() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'disable' | 'enable' | 'reset-two-factor' }) =>
      http.post<Resource<Staff>>(`/staff/${id}/${action}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['staff'] }),
  })
}

export function useInviteStaff() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { email: string; name: string; role: StaffRole }) =>
      http.post<Resource<StaffInvitation>>('/staff/invitations', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['staff'] }),
  })
}

export function useRevokeInvitation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => http.delete(`/staff/invitations/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['staff'] }),
  })
}

export function useChangeRole() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, role }: { id: string; role: StaffRole }) =>
      http.patch<Resource<Staff>>(`/staff/${id}/role`, { role }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['staff'] }),
  })
}
